<template>
  <div :class="$style.syncCalendarModalInstruction">
    <el-tabs
      v-if="methods.length > 1"
      v-model="currentTabIdx"
      type="card"
      :class="$style.tabs"
    >
      <el-tab-pane
        v-for="method in methods"
        :key="method.value"
        :label="method.label"
      />
    </el-tabs>

    <p v-if="description" :class="$style.description">{{ description }}</p>

    <ul :class="$style.list">
      <li
        v-for="(step, idx) in steps"
        :key="idx"
        :class="[
          $style.item,
          { [$style._isCopy]: step.type === 'copy' },
          { [$style._isOneStep]: isOneStep },
        ]"
      >
        <span v-show="!isOneStep" :class="$style.number">{{ idx + 1 }}</span>

        <div v-html="step.desc" :class="$style.desc"></div>

        <div v-if="step.type === 'copy'" :class="$style.copyBlock">
          <el-input :class="$style.input" :value="url" readonly />
          <el-button :class="$style.button" icon="el-icon-document-copy" @click="onCopy" />
        </div>
      </li>
    </ul>

    <div v-if="gif" :class="[$style.imageWrap, {[$style._isFull]: imageIsFull}]">
      <img
        :src="require(`@/assets/img/syncCalendarGif/${gif}`)"
        :class="$style.image"
        alt="instruction"
      />
      <el-button :class="$style.fullscreen" class="btn-fill" @click="imageIsFull = !imageIsFull">
        <i :class="[$style.fullscreenIcon, imageIsFull ? 'el-icon-close' : 'el-icon-full-screen']" />
      </el-button>
    </div>
  </div>
</template>

<script>

import helperFunctions from "@/globalFunctions";

export default {
  name: "syncCalendarModalInstruction",

  props: {
    methods: {
      type: Array,
      default: () => [],
    },

    url: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      currentTabIdx: 0,
      imageIsFull: false,
    };
  },

  watch: {
    currentTabIdx(newValue) {
      this.$emit("select-method", newValue);
    },
  },

  computed: {
    currentTab() {
      return this.methods[this.currentTabIdx];
    },

    description() {
      return this.currentTab?.desc ?? "";
    },

    steps() {
      return this.currentTab?.steps ?? [];
    },

    gif() {
      return this.currentTab?.gif ?? "";
    },

    isOneStep() {
      return this.currentTab?.steps?.length === 1;
    },
  },

  methods: {
    onCopy() {
      const { copyText } = helperFunctions;

      copyText(this.url);

      this.notify(
        true,
        "Текст скопирован",
      );
    },

    notify(status, text){
          const h = this.$createElement
          this.$notify({
              title: '',
              message: h('i', { style: `color: ${status ?  '#67C23A' : ' #f56c6c' }` }, text)
          });
      }
  },
};
</script>

<style lang="scss" module>
.syncCalendarModalInstruction {
  .icon {
    font-size: 16px;
    fill: $basic-white;
  }

  .input {
    width: 100%;
    max-width: unset;

    :global(.m-input__input) {
      color: $basic-black;
    }
  }

  .tabs {
    margin-bottom: 20px;

    :global(.el-tabs__item) {
      color: $brand-clr-1;
    }

    :global(.el-tabs__nav) {
      border-radius: 10px 10px 0 0;
    }

    :global(.el-tabs__item:first-child) {
      border-radius: 10px 0 0 0;
    }

    :global(.el-tabs__item:last-child) {
      border-radius: 0 10px 0 0;
    }

    :global(.el-tabs__item.is-active) {
      background-color: $brand-clr-1;
      color: $basic-white;
    }
  }

  .fullscreen {
    position: absolute;
    right: 20px;
    bottom: 20px;
    width: 40px;
    height: 40px;
    padding: 5px;
    transition-duration: .3s;
    transition-property: left, right;

    .fullscreenIcon {
      font-size: 20px;
      fill: $basic-white;
    }
  }
}

.list {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.item {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas: "number desc";
  column-gap: 20px;
  row-gap: 8px;

  &:not(:last-child) {
    margin-bottom: 24px;
  }

  &._isCopy {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "number desc"
      ". copy";
  }

  &._isOneStep {
    grid-template-columns: 1fr;
    grid-template-areas: "desc";
  }

  &._isOneStep._isCopy {
    justify-items: flex-start;
    grid-template-columns: 1fr;
    grid-template-areas: "desc" "copy";
  }
}

.number {
  grid-area: number;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: $basic-white;
  border-radius: 50%;
  background-color: $brand-clr-1;
}

.description {
  margin-bottom: 20px;
  font-size: 16px;
}

.desc {
  grid-area: desc;
  margin-top: 4px;
  font-size: 16px;

  svg {
    color: $basic-black;
    transform: translateY(2px);
  }

  p {
    margin: 0;
  }

  b {
    color: $basic-black;
  }
}

.copyBlock {
  grid-area: copy;
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 6px;
  width: 100%;
}

.button {
  padding: 5px;
  width: 40px;
  height: 40px;

  &:global(.el-button) {
    padding: 5px;
  } 
}

.image {
  margin-top: 40px;
  width: 100%;
}

.imageWrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &._isFull {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 20;
    width: 100%;
    height: 100%;

    .image {
      width: 90%;
      height: 90%;
      margin: auto;
      object-fit: contain;
    }

    .fullscreen {
      top: 20px;
      bottom: unset;
    }
  }
}
</style>
